import CloseIcon from '@mui/icons-material/Close';
import FmdBadIcon from '@mui/icons-material/FmdBad';
import { Box, Checkbox, DialogTitle, FormControl, FormControlLabel, Grid, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import dayjs from 'dayjs';
import { FC } from 'react';
import { ITheme } from '../../../utils/theme';
import { IOption, IPreference, IShift, IToggle } from '../TelephonePlugInData';
import { ExpiredIconStyle, LastChangeStyle, ReviewBlockStyle, ReviewModalStyle, ReviewModalTitleStyle, ReviewPrefStyle, reviewModalCheckboxStyle } from './styled';

export enum ReviewModalStatus {
  NotPluggedIn = "Not Plugged-In",
  PluggedIn = "Plugged-In"
}

export enum ReviewModalMode {
  Blank = "blank",
  View = 'view',
  Submit = "submit",
}

export interface IReviewModalProps {
  open: boolean,
  onClose: () => void,
  shift?: IShift,
  isPluggedIn: boolean,
  mode: ReviewModalMode,
  options: IOption[];
  toggles: IToggle[];
  preferences: IPreference[];
  onSubmit: () => void,
  lastChange?: string,
  confNum?: string,
  status: ReviewModalStatus,
  loading: boolean,
}

export const ReviewModal: FC<IReviewModalProps> = ({
  open,
  onClose,
  mode,
  shift = { display: '', value: '' },
  isPluggedIn,
  options,
  toggles,
  preferences,
  onSubmit,
  lastChange,
  confNum,
  status,
  loading
}) => {
  const disabled = mode === ReviewModalMode.Blank || mode === ReviewModalMode.View;
  const [time, date] = shift?.value.split(',');
  const formattedTime = time.replace(/(\d{2})(\d{2})/, '$1:$2');
  const formattedDate = dayjs(date, 'DDMMYYYY').format('ddd. MMM D, YYYY');
  const noContent = mode === ReviewModalMode.Blank || (!options.length && !toggles.length);
  const isSubmitMode = mode === ReviewModalMode.Submit;
  return (
    <Dialog
      sx={ReviewModalStyle}
      open={open}
      onClose={onClose}
      className={mode}
    >
      <DialogTitle sx={ReviewModalTitleStyle}>
        <Box>
          <Typography sx={{ fontWeight: 'bold', color: 'black' }} component='span'>Status: </Typography>
          <Typography sx={{
            fontWeight: 'bold',
            color: (theme: ITheme) =>
              status === ReviewModalStatus.PluggedIn ? theme.palette.success.light : theme.palette.grey.A150
          }}
            component='span'
          >
            {status}
          </Typography>
        </Box>
        <CloseIcon onClick={onClose} />
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={ReviewBlockStyle}>
          <Typography>
            You {isPluggedIn ? 'have' : 'are not'} plugged-in for the {formattedTime} shift on {formattedDate}
          </Typography>
        </Box>
        {noContent && <Box sx={ReviewBlockStyle} className='minHeight' />}
        {loading && <CircularProgress />}
        {mode !== ReviewModalMode.Blank && <>
          {(options.length || toggles.length) ? <Box sx={ReviewBlockStyle}>
            <Typography sx={{ fontWeight: 'bold' }}>Your selected plugin options and toggles are:</Typography>
            {
              options.map(o => (
                <FormControl sx={reviewModalCheckboxStyle} key={o.code}>
                  <FormControlLabel
                    control={<Checkbox checked={o.isChecked} />}
                    label={o.description}
                    disabled={true}
                  />
                </FormControl>
              ))
            }
            {
              toggles.map(t => (
                <FormControl sx={reviewModalCheckboxStyle} key={t.code}>
                  <FormControlLabel
                    control={<Checkbox checked={t.toggleCodeChecked} />}
                    label={t.description}
                    disabled={true}
                  />
                  {t.squamishSubBoards?.filter(sb => sb.isChecked).length ? (
                    <>
                      <Typography sx={{ fontWeight: 'bold', mt: 1 }}>Your selected {t.description} for the following categories:</Typography>
                      <Grid container>
                        {t.squamishSubBoards.filter(sb => sb.isChecked).map(sb => (
                          <Grid item xs={6} key={sb.description}>
                            <FormControl sx={reviewModalCheckboxStyle} key={sb.description}>
                              <FormControlLabel
                                control={<Checkbox
                                  checked={sb.isChecked}
                                />}
                                label={`${sb.description}${sb.sequence}`}
                                disabled={true}
                              />
                            </FormControl>
                          </Grid>
                        ))}
                      </Grid>
                    </>) : null}

                  <Typography sx={{ ml: 4 }}>
                    <FmdBadIcon sx={ExpiredIconStyle} />
                    Expires {t.expireDateTime}
                  </Typography>
                </FormControl>
              ))
            }
          </Box> : null}
          {preferences.length ? <Box sx={ReviewBlockStyle}>
            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Your selected Plugin Preferences are:</Typography>
            {
              preferences.map(p => (
                <Box sx={ReviewPrefStyle} key={p.code}>
                  <img
                    src={`/assets/images/grey_slip.png`}
                    alt="GREY SLIP"
                    width={20}
                    height={40}
                    style={{ marginRight: '8px' }}
                  />
                  <Typography>
                    <Typography component='span' sx={{ fontWeight: 'bold' }}>{p.code}</Typography> {p.description}
                  </Typography>
                </Box>
              ))
            }
          </Box> : null}
        </>}
      </DialogContent>
      <DialogActions sx={{ display: 'block' }}>
        <Box sx={{ display: 'flex' }}>
          <Typography sx={{ width: '100px', whiteSpace: 'nowrap' }}>Last Change:</Typography>
          <Typography className='lastChange' sx={LastChangeStyle}>
            {isSubmitMode ? 'Not Submitted' : lastChange && `${lastChange} - mybcmea.com`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex' }}>
          <Typography sx={{ width: '100px', whiteSpace: 'nowrap' }}>Conf. No.:</Typography>
          <Typography sx={LastChangeStyle}>{isSubmitMode || !confNum ? '' : `#${confNum}`}</Typography>
        </Box>
        <Box>
          <Button variant="contained" sx={{ width: '100%' }} disabled={disabled} onClick={onSubmit}>
            Submit
          </Button>
        </Box>
      </DialogActions>
    </Dialog >
  )
}